const REQUESTS_STORAGE_KEY = 'asi:subscription-requests';
const SUBSCRIPTIONS_STORAGE_KEY = 'asi:user-subscriptions';
const REQUESTS_EVENT = 'asi:subscription-requests-changed';
const SUBSCRIPTIONS_EVENT = 'asi:subscription-updated';

function isBrowser() {
  return typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';
}

function readJson(key, fallback) {
  if (!isBrowser()) {
    return fallback;
  }

  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) {
      return fallback;
    }
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object') {
      return parsed;
    }
    return fallback;
  } catch (error) {
    return fallback;
  }
}

function writeJson(key, value) {
  if (!isBrowser()) {
    return;
  }

  window.localStorage.setItem(key, JSON.stringify(value));
}

function dispatchEvent(eventName, detail) {
  if (!isBrowser()) {
    return;
  }

  if (typeof window.CustomEvent === 'function') {
    const event = new CustomEvent(eventName, { detail });
    window.dispatchEvent(event);
    return;
  }

  const event = document.createEvent('Event');
  event.initEvent(eventName, false, false);
  event.detail = detail;
  window.dispatchEvent(event);
}

function persistRequests(requests) {
  writeJson(REQUESTS_STORAGE_KEY, requests);
  dispatchEvent(REQUESTS_EVENT, { requests });
}

function persistSubscriptions(subscriptionsMap) {
  writeJson(SUBSCRIPTIONS_STORAGE_KEY, subscriptionsMap);
  dispatchEvent(SUBSCRIPTIONS_EVENT, { subscriptions: subscriptionsMap });
}

function getRequestIndex(requests, id) {
  return requests.findIndex((item) => item.id === id);
}

export function getAllRequests() {
  return readJson(REQUESTS_STORAGE_KEY, []);
}

export function getRequestsForUser(userId) {
  return getAllRequests().filter((request) => request.userId === userId);
}

export function getPendingRequestForUser(userId) {
  return getRequestsForUser(userId).find((request) => request.status === 'pending') || null;
}

function generateRequestId() {
  return `req_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

export function upsertPendingRequest({
  userId,
  userName,
  userEmail,
  planId,
  planName,
  productId,
  planPriceDisplay,
  planPriceAmount,
  planBillingPeriod,
  planBillingUnit,
  planFeatures,
}) {
  if (!userId || !planId) {
    throw new Error('userId and planId are required to submit a subscription request.');
  }

  const now = new Date().toISOString();
  const requests = getAllRequests();
  const existingIndex = requests.findIndex(
    (request) => request.userId === userId && request.status === 'pending',
  );

  if (existingIndex >= 0) {
    const updatedRequest = {
      ...requests[existingIndex],
      planId,
      planName,
      userName,
      userEmail: userEmail || requests[existingIndex].userEmail || null,
      productId: productId || requests[existingIndex].productId || null,
      planPriceDisplay:
        planPriceDisplay || requests[existingIndex].planPriceDisplay || null,
      planPriceAmount:
        planPriceAmount !== undefined
          ? planPriceAmount
          : requests[existingIndex].planPriceAmount || null,
      planBillingPeriod:
        planBillingPeriod !== undefined
          ? planBillingPeriod
          : requests[existingIndex].planBillingPeriod || null,
      planBillingUnit:
        planBillingUnit || requests[existingIndex].planBillingUnit || null,
      planFeatures: Array.isArray(planFeatures)
        ? planFeatures
        : Array.isArray(requests[existingIndex].planFeatures)
        ? requests[existingIndex].planFeatures
        : [],
      submittedAt: now,
    };
    requests[existingIndex] = updatedRequest;
    persistRequests(requests);
    return updatedRequest;
  }

  const newRequest = {
    id: generateRequestId(),
    userId,
    userName,
    userEmail: userEmail || null,
    productId: productId || null,
    planPriceDisplay: planPriceDisplay || null,
    planPriceAmount: planPriceAmount !== undefined ? planPriceAmount : null,
    planBillingPeriod: planBillingPeriod !== undefined ? planBillingPeriod : null,
    planBillingUnit: planBillingUnit || null,
    planFeatures: Array.isArray(planFeatures)
      ? planFeatures.filter((feature) => typeof feature === 'string' && feature.trim())
      : [],
    planId,
    planName,
    status: 'pending',
    submittedAt: now,
  };
  requests.push(newRequest);
  persistRequests(requests);
  return newRequest;
}

function updateRequest(id, patch) {
  const requests = getAllRequests();
  const index = getRequestIndex(requests, id);
  if (index === -1) {
    return null;
  }

  const updated = {
    ...requests[index],
    ...patch,
  };
  requests[index] = updated;
  persistRequests(requests);
  return updated;
}

export function cancelRequest(id) {
  return updateRequest(id, {
    status: 'cancelled',
    resolvedAt: new Date().toISOString(),
  });
}

export function approveRequest(id) {
  const approvedAt = new Date().toISOString();
  const updated = updateRequest(id, {
    status: 'approved',
    resolvedAt: approvedAt,
  });

  if (updated) {
    setSubscription(updated.userId, {
      planId: updated.planId,
      planName: updated.planName,
      productId: updated.productId || null,
      approvedAt,
      planPriceDisplay: updated.planPriceDisplay || null,
      planPriceAmount: updated.planPriceAmount || null,
      planBillingPeriod: updated.planBillingPeriod || null,
      planBillingUnit: updated.planBillingUnit || null,
      planFeatures: Array.isArray(updated.planFeatures) ? updated.planFeatures : [],
      userName: updated.userName || null,
      userEmail: updated.userEmail || null,
      updatedAt: approvedAt,
    });
  }

  return updated;
}

export function declineRequest(id) {
  return updateRequest(id, {
    status: 'declined',
    resolvedAt: new Date().toISOString(),
  });
}

export function removeRequest(id) {
  const requests = getAllRequests();
  const index = getRequestIndex(requests, id);
  if (index === -1) {
    return false;
  }

  requests.splice(index, 1);
  persistRequests(requests);
  return true;
}

export function getSubscriptionsMap() {
  return readJson(SUBSCRIPTIONS_STORAGE_KEY, {});
}

export function getSubscription(userId) {
  const map = getSubscriptionsMap();
  return map[userId] || null;
}

export function setSubscription(userId, subscription) {
  if (!userId) {
    throw new Error('userId is required to store a subscription.');
  }

  const map = getSubscriptionsMap();
  const existing = map[userId] || {};
  const normalizedFeatures = Array.isArray(subscription.planFeatures)
    ? subscription.planFeatures
    : Array.isArray(existing.planFeatures)
    ? existing.planFeatures
    : [];
  const updatedSubscription = {
    ...existing,
    ...subscription,
    planId: subscription.planId,
    planName: subscription.planName,
    planFeatures: normalizedFeatures,
    planPriceDisplay: subscription.planPriceDisplay || existing.planPriceDisplay || null,
    planPriceAmount:
      subscription.planPriceAmount !== undefined
        ? subscription.planPriceAmount
        : existing.planPriceAmount || null,
    planBillingPeriod:
      subscription.planBillingPeriod !== undefined
        ? subscription.planBillingPeriod
        : existing.planBillingPeriod || null,
    planBillingUnit:
      subscription.planBillingUnit || existing.planBillingUnit || null,
    updatedAt: new Date().toISOString(),
  };
  map[userId] = updatedSubscription;
  persistSubscriptions(map);
  return updatedSubscription;
}

export function ensureSubscription(userId, fallbackSubscription) {
  const existing = getSubscription(userId);
  if (existing) {
    return existing;
  }

  if (!fallbackSubscription) {
    return null;
  }

  return setSubscription(userId, fallbackSubscription);
}

export function subscribeToRequestChanges(callback) {
  if (!isBrowser()) {
    return () => {};
  }

  const handler = (event) => {
    const detail = event && event.detail && event.detail.requests;
    if (detail) {
      callback(detail);
    } else {
      callback(getAllRequests());
    }
  };

  window.addEventListener(REQUESTS_EVENT, handler);
  return () => window.removeEventListener(REQUESTS_EVENT, handler);
}

export function subscribeToSubscriptionChanges(callback) {
  if (!isBrowser()) {
    return () => {};
  }

  const handler = (event) => {
    const detail = event && event.detail && event.detail.subscriptions;
    if (detail) {
      callback(detail);
    } else {
      callback(getSubscriptionsMap());
    }
  };

  window.addEventListener(SUBSCRIPTIONS_EVENT, handler);
  return () => window.removeEventListener(SUBSCRIPTIONS_EVENT, handler);
}

export function refreshRequests() {
  return getAllRequests();
}

export function refreshSubscriptions() {
  return getSubscriptionsMap();
}

export default {
  getAllRequests,
  getRequestsForUser,
  getPendingRequestForUser,
  upsertPendingRequest,
  cancelRequest,
  approveRequest,
  declineRequest,
  removeRequest,
  getSubscription,
  setSubscription,
  ensureSubscription,
  subscribeToRequestChanges,
  subscribeToSubscriptionChanges,
  refreshRequests,
  refreshSubscriptions,
};
